import { useEffect, useRef, useState, useCallback } from "react";
import { api } from "@/lib/api";

/**
 * Simple in-memory cache keyed by request path. Lives for the lifetime of the
 * tab. When the user navigates About → Home the second time, `/pages/home` is
 * served instantly from this cache — no null-flash of the hero, no stale
 * fallback ("tagline") appearing before the real hero title.
 *
 * Data still refreshes in the background (stale-while-revalidate) so an admin
 * update in another tab is picked up on the next navigation.
 */
const CACHE = new Map();

// Invalidate a specific key or a matching prefix — useful when an admin edit
// happens in the same tab (call from admin PUT handlers).
export function invalidateFetchCache(prefix) {
  if (!prefix) { CACHE.clear(); return; }
  for (const k of Array.from(CACHE.keys())) {
    if (k.startsWith(prefix)) CACHE.delete(k);
  }
}

/**
 * Fetch helper with:
 *  - Stable in-memory cache (returns cached data as initial state → no flash)
 *  - Stale-while-revalidate background refetch on remount
 *  - Auto-retry with exponential backoff on network / 5xx errors
 *  - AbortController cancellation on unmount
 *  - Manual `refetch` that also updates the cache
 *
 * NOTE: `deps` should include any values already interpolated INTO the `path`
 * string (e.g. `[page]` when path is `/services?page=${page}`).
 */
export function useFetch(path, deps = []) {
  const cached = CACHE.get(path);
  const [data, setData] = useState(cached ?? null);
  const [loading, setLoading] = useState(!cached);
  const [error, setError] = useState(null);
  const controllerRef = useRef(null);

  const run = useCallback(async () => {
    controllerRef.current?.abort?.();
    const controller = new AbortController();
    controllerRef.current = controller;
    // If we already have a cached value, keep it visible during background revalidation.
    if (!CACHE.has(path)) setLoading(true);
    setError(null);

    const maxAttempts = 3;
    for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
      try {
        const r = await api.get(path, { signal: controller.signal });
        if (controller.signal.aborted) return;
        CACHE.set(path, r.data);
        setData(r.data);
        setError(null);
        setLoading(false);
        return;
      } catch (e) {
        if (controller.signal.aborted) return;
        const status = e?.response?.status;
        const isNetwork = !e?.response;
        const retryable = isNetwork || (typeof status === "number" && status >= 500);
        if (!retryable || attempt === maxAttempts) {
          if (controller.signal.aborted) return;
          setError(e);
          setLoading(false);
          return;
        }
        const delay = 500 * 2 ** (attempt - 1) + Math.floor(Math.random() * 300);
        await new Promise((res) => setTimeout(res, delay));
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => {
    run();
    return () => controllerRef.current?.abort?.();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { data, loading, error, refetch: run };
}
